import { Alert, Stack } from '@rossum/ui/material';
import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { DateTimeRangePicker } from '../../../../document-list-base/components/DateTimeRangePicker';
import {
  getCustomDateRangeTime,
  getCustomFixedDateTime,
} from '../../../../document-list-base/components/DateTimeRangePicker/shortcuts';
import {
  getValidatedDateContext,
  isDateOperator,
  isRangedDateOperator,
  processDatesBeforeSubmit,
  SelectedDates,
  shouldBeEndOfDay,
} from '../../../../document-list-base/mql/dateUtils';
import { FilterOperator } from '../../../../document-list-base/mql/operators';
import { resolveValueBasedOnBooleanOperator } from '../../../../document-list-base/mql/operatorUtils';
import { FilterComponentProps } from '../../../../document-list-base/mql/types';
import { useOperators } from '../../../hooks/useOperators';
import { FilterActionsWrapper } from '../FilterActionsWrapper';

export const DateTimePicker = ({
  applyFilter,
  filterItem,
  onClose,
  onBackClick,
}: FilterComponentProps) => {
  const intl = useIntl();
  const [hasWarning, setHasWarning] = useState<boolean>(false);

  const { operatorValue, existingDates } = getValidatedDateContext(filterItem);

  const [selectedDates, setSelectedDates] =
    useState<SelectedDates>(existingDates);

  const firstDateIsNull = selectedDates[0] === null;
  const secondDateIsNull = selectedDates[1] === null;
  const areDatesNull = firstDateIsNull && secondDateIsNull;

  if (!operatorValue && !hasWarning && areDatesNull) {
    setHasWarning(true);
  }

  useEffect(() => {
    if (!areDatesNull) setHasWarning(false);
  }, [areDatesNull]);

  const handleOperatorChange = useCallback(
    (operator: FilterOperator) => {
      const firstDate = selectedDates[0];

      if (!firstDate) return;

      if (!isRangedDateOperator(operator.value)) {
        const fixedDate = getCustomFixedDateTime(
          firstDate,
          shouldBeEndOfDay(operator.value)
        );
        // update selectedDate to endOf or startOf the day based on the operator
        if (fixedDate !== firstDate) setSelectedDates([fixedDate, fixedDate]);
        return;
      }

      setSelectedDates(getCustomDateRangeTime([firstDate, firstDate], true));
    },
    [selectedDates]
  );

  const {
    node: Operators,
    operator,
    emptyOperatorIsUsed,
  } = useOperators({
    filterItem,
    initialState: {
      value: operatorValue ?? 'is',
      type: 'date',
      component: 'dateTimePicker',
    },
    onChange: handleOperatorChange,
  });

  const isRangedOperator = isRangedDateOperator(operator.value);

  const dateOperator = isDateOperator(operator.value) ? operator.value : null;

  const areRangedValuesInvalid =
    (isRangedOperator && secondDateIsNull) ||
    (!isRangedOperator && firstDateIsNull);

  return (
    <FilterActionsWrapper
      onSubmit={() => {
        if (emptyOperatorIsUsed) {
          applyFilter({
            operator,
            value: resolveValueBasedOnBooleanOperator(operator),
          });
        }

        const processedFilterContext = processDatesBeforeSubmit({
          selectedDates,
          operator,
          column: filterItem.column,
        });

        if (processedFilterContext) applyFilter(processedFilterContext);
      }}
      isSubmitDisabled={!emptyOperatorIsUsed && areRangedValuesInvalid}
      onCancel={onClose}
      onBackClick={onBackClick}
      columnField={filterItem.column.field}
      hasFixedWidth={emptyOperatorIsUsed}
    >
      <Stack>
        <Operators />
        {hasWarning && (
          <Alert
            color="warning"
            closeText="Close"
            sx={{ background: 'transparent' }}
          >
            {intl.formatMessage({
              id: 'containers.filtering.invalidDateFilter',
            })}
          </Alert>
        )}
        {!emptyOperatorIsUsed && dateOperator ? (
          <DateTimeRangePicker
            selectedDates={selectedDates}
            setSelectedDates={setSelectedDates}
            operatorValue={dateOperator}
          />
        ) : null}
      </Stack>
    </FilterActionsWrapper>
  );
};
